import fp from 'lodash/fp';
import React, { FC, forwardRef } from 'react';

import { Tooltip, Typography, TypographyProps, useDesktop } from 'Modules/ui3';

import { CellProps } from '../types';
import { EmissionStatus } from '../../../api';
import { DfaStatus } from '../../DfaStatus';
import { DfaStatusesModel } from '../../DfaStatusesModel';


export const EmissionTypography: typeof Typography = forwardRef((props, ref) => {
    const isDesktop = useDesktop();
    return (
        <Typography
            ref={ref}
            variant={isDesktop ? 'regularBody2' : 'regularCaption3'}
            mt={0.5}
            {...props}
        />
    );
});

const DocumentLink: FC<TypographyProps<'a'>> = props => (
    <EmissionTypography
        component='a'
        target='_blank'
        sx={{
            '&:visited': {
                color: 'general.primary50',
            },
        }}
        {...props}
    />
);

export const IssuerCell: FC<CellProps> = ({ data, column: { field } }) => {
    const text = fp.get(field, data);
    return (
        <Tooltip
            text={text}
            placement='top-start'
            textProps={{
                variant: 'regularCaption2',
            }}
        >
            <EmissionTypography
                color='grayscale.shade95'
                overflow='hidden'
                textOverflow='ellipsis'
                sx={{
                    '&:hover': {
                        color: 'general.primary50',
                    },
                }}
            >
                {text}
            </EmissionTypography>
        </Tooltip>
    );
};


export const StatusCell: FC<CellProps> = ({ data }) => {
    if (!Object.values(EmissionStatus).includes(data.status))
        return null;

    return (
        <Tooltip
            placement='top'
            background='grayscale.shade0'
            componentPropsTooltipSx={{
                p: 4,
                borderRadius: 4,
                boxShadow: '0px 0px 80px 0px rgba(0, 0, 0, 0.08)',
            }}
            content={<DfaStatusesModel status={data.status} customText={data.statusextended} />}
        >
            <DfaStatus status={data.status} customText={data.statusextended} />
        </Tooltip>
    );
};

export const StringCell: FC<CellProps> = ({ data, column: { field } }) => {
    const text = fp.get(field, data) as string;
    return (
        <EmissionTypography
            component='span'
            color='grayscale.shade95'
            title={text}
        >
            {text}
        </EmissionTypography>
    );
};

export const DocumentCell: FC<CellProps> = ({ data, column: { field } }) => {
    const url = fp.get(`${field}.file.url`, data);
    if (!url)
        return null;

    const text = fp.get(`${field}.caption`, data);
    return (
        <DocumentLink href={url} title={text}>
            {text}
        </DocumentLink>
    );
};

export const SignCell: FC<CellProps> = ({ data, column: { field }, t }) => {
    const url = fp.get(`${field}.file.url`, data);
    if (!url)
        return null;

    const text = fp.get(`${field}.caption`, data);
    return (
        <DocumentLink href={url} title={text}>
            {t('sign')}
        </DocumentLink>
    );
};
